import { Service, ServiceStatus } from "../models/Service";
import { Message, MessageStatus } from "../models/Message";
import { User, UserRole } from "../models/User";
import { normalizeMessage, normalizeService } from "../services/messageAdapters";
import { PresenceItem } from "../models/Presence";
import { getAssignedRole } from "../services/accessControl";

const normalizeSearch = (value?: string | null): string => (value || "").trim().toLowerCase();

const normalizeDigits = (value?: string | null): string => (value || "").replace(/\D/g, "");

const latestServiceTimestamp = (service: Service): number => {
  const candidate =
    service.chat?.messages?.[0]?.createdAt ||
    service.updatedAt ||
    service.createdAt;

  const parsed = candidate ? new Date(candidate).getTime() : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const messageTimestamp = (message: Message): number => {
  const parsed = message.createdAt ? new Date(message.createdAt).getTime() : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortMessagesByNewest = (messages: Array<Message>): Array<Message> => [ ...messages ].sort(( firstMessage, secondMessage ) => {
  const timestampDiff = messageTimestamp( secondMessage ) - messageTimestamp( firstMessage );
  if( timestampDiff !== 0 ) return timestampDiff;
  return secondMessage.id.localeCompare( firstMessage.id );
});

export const sortRealtimeServices = (services: Array<Service>): Array<Service> => {
  return [ ...services ].sort(( firstService, secondService ) => {
    return latestServiceTimestamp( secondService ) - latestServiceTimestamp( firstService );
  });
};

export const applyPresenceToService = ( service: Service, presence: PresenceItem ): Service => {
  if( presence.type === "user" && service.user?.id === presence.id ) {
    return {
      ...service,
      user: {
        ...service.user,
        isOnline: presence.isOnline,
      },
    };
  }

  if( presence.type === "contact" && service.chat?.contact?.id === presence.id ) {
    return {
      ...service,
      chat: {
        ...service.chat,
        contact: {
          ...service.chat.contact,
          isOnline: presence.isOnline,
        },
      },
    };
  }

  return service;
};

export const applyPresenceToServices = ( services: Array<Service>, presence: PresenceItem ): Array<Service> => (
  services.map(( service ) => applyPresenceToService( service, presence ))
);

const messageStatusRank = ( status?: MessageStatus ): number => {
  switch( status ) {
    case MessageStatus.READ:
      return 4;
    case MessageStatus.DELIVERED:
      return 3;
    case MessageStatus.SENT:
      return 2;
    case MessageStatus.SENDING:
      return 1;
    default:
      return 0;
  }
};

const mergeMessageStatus = ( currentMessage: Message, incomingMessage: Message ): MessageStatus => {
  if( incomingMessage.status === MessageStatus.FAILED ) {
    return currentMessage.status === MessageStatus.DELIVERED || currentMessage.status === MessageStatus.READ
      ? currentMessage.status
      : MessageStatus.FAILED;
  }

  if( currentMessage.status === MessageStatus.FAILED ) {
    return incomingMessage.status === MessageStatus.SENDING
      ? currentMessage.status
      : incomingMessage.status;
  }

  return messageStatusRank( currentMessage.status ) > messageStatusRank( incomingMessage.status )
    ? currentMessage.status
    : incomingMessage.status;
};

const mergeMessageReactions = ( currentMessage: Message, incomingMessage: Message ): Message["reactions"] => {
  if( incomingMessage.reactions !== undefined ) return incomingMessage.reactions;
  return currentMessage.reactions;
};

const isEquivalentOptimisticMessage = ( message: Message, persistedMessage: Message ): boolean => {
  return message.status === MessageStatus.SENDING &&
    message.id !== persistedMessage.id &&
    message.ownerType === persistedMessage.ownerType &&
    message.messageType === persistedMessage.messageType &&
    ( message.content ?? "" ) === ( persistedMessage.content ?? "" );
};

export const mergeRealtimeMessages = (
  messages: Array<Message>,
  incomingMessages: Array<Message>,
): Array<Message> => {
  const mergedMessages = incomingMessages.reduce(( currentMessages, incomingMessage ) => {
    const exactMatch = currentMessages.find(( message ) => message.id === incomingMessage.id );

    if( exactMatch ) {
      return currentMessages.map(( message ) => {
        if( message.id !== incomingMessage.id ) return message;

        return {
          ...message,
          ...incomingMessage,
          status: mergeMessageStatus( message, incomingMessage ),
          reactions: mergeMessageReactions( message, incomingMessage ),
        };
      });
    }

    const hasEquivalentOptimisticMessage = currentMessages.some(( message ) => isEquivalentOptimisticMessage( message, incomingMessage ));

    if( hasEquivalentOptimisticMessage ) {
      let inserted = false;

      return currentMessages.reduce(( reconciledMessages, message ) => {
        if( isEquivalentOptimisticMessage( message, incomingMessage )) {
          if( !inserted ) {
            reconciledMessages.push( incomingMessage );
            inserted = true;
          }

          return reconciledMessages;
        }

        reconciledMessages.push( message );

        return reconciledMessages;
      }, [] as Array<Message>);
    }

    return [ incomingMessage, ...currentMessages ];
  }, messages);

  return sortMessagesByNewest( mergedMessages );
};

const messageMatchesStatusKey = (message: Message, messagesKeys: Array<string>): boolean => {
  return messagesKeys.includes( message.id ) ||
    Boolean( message.providerMessageId && messagesKeys.includes( message.providerMessageId )) ||
    Boolean( message.externalMessageId && messagesKeys.includes( message.externalMessageId ));
};

export const updateMessagesStatus = (
  messages: Array<Message> | undefined,
  messagesKeys: Array<string>,
  status: MessageStatus,
): Array<Message> => (
  messages?.map(( message ) => messageMatchesStatusKey( message, messagesKeys )
    ? {
      ...message,
      status: mergeMessageStatus( message, { ...message, status }),
    }
    : message
  ) ?? []
);

export const reconcileOptimisticMessage = (
  messages: Array<Message>,
  optimisticMessageID: string,
  persistedMessage: Message,
): Array<Message> => {
  const existingPersistedMessage = messages.find(( message ) => message.id === persistedMessage.id && message.id !== optimisticMessageID );
  const replacement = existingPersistedMessage
    ? {
      ...existingPersistedMessage,
      ...persistedMessage,
      status: mergeMessageStatus( existingPersistedMessage, persistedMessage ),
    }
    : persistedMessage;
  const reconciledMessages: Array<Message> = [];
  let inserted = false;

  messages.forEach(( message ) => {
    if( message.id === optimisticMessageID || isEquivalentOptimisticMessage( message, persistedMessage )) {
      if( !inserted ) {
        reconciledMessages.push( replacement );
        inserted = true;
      }

      return;
    }

    if( message.id === persistedMessage.id ) {
      if( !inserted && !messages.some(( currentMessage ) => currentMessage.id === optimisticMessageID )) {
        reconciledMessages.push( replacement );
        inserted = true;
      }

      return;
    }

    reconciledMessages.push( message );
  });

  if( !inserted ) return [ replacement, ...reconciledMessages ];

  return reconciledMessages;
};

export const mergeSelectedRealtimeService = (
  selectedService: Service,
  incomingService: Service,
): Service => {
  const selectedChannel = selectedService.chat.channel;
  const incomingChannel = incomingService.chat.channel;
  const nextChannel = incomingChannel?.name ? incomingChannel : selectedChannel ?? incomingChannel;
  const nextQueue = mergeServiceListQueue(selectedService, incomingService);
  const nextUser = mergeServiceListUser(selectedService, incomingService);
  const nextContact = mergeServiceListContact(selectedService, incomingService);

  return {
    ...selectedService,
    ...incomingService,
    queue: nextQueue,
    user: nextUser,
    chat: {
      ...selectedService.chat,
      ...incomingService.chat,
      channel: nextChannel,
      channelId: incomingService.chat?.channelId || selectedService.chat.channelId || nextChannel?.id,
      contact: nextContact,
      messages: selectedService.chat.messages,
    },
  };
};

export const serviceMatchesRealtimeFilters = (
  service: Service,
  status: ServiceStatus,
  queueId?: string,
  search?: string,
  channelId?: string,
): boolean => {
  if( service.status !== status ) return false;
  if( queueId && service.queue?.id !== queueId ) return false;
  if( channelId && service.chat?.channelId !== channelId && service.chat?.channel?.id !== channelId && service.chat?.channel?.id !== channelId ) return false;

  const normalizedSearch = normalizeSearch( search );
  if( !normalizedSearch ) return true;

  const normalizedDigits = normalizeDigits( normalizedSearch );
  const contactName = normalizeSearch( service.chat?.contact?.name );
  const contactPhone = normalizeDigits( service.chat?.contact?.phone );

  if( contactName.includes( normalizedSearch )) return true;
  if( normalizedDigits && contactPhone.includes( normalizedDigits )) return true;

  return false;
};

const mergeServiceListChannel = (existingService: Service, incomingService: Service) => {
  const existingChannel = existingService.chat?.channel;
  const incomingChannel = incomingService.chat?.channel;

  if( !incomingChannel ) return existingChannel;
  if( incomingChannel.name ) return incomingChannel;
  if( !existingChannel?.name ) return incomingChannel;
  if( incomingChannel.id && existingChannel.id && incomingChannel.id !== existingChannel.id ) return incomingChannel;

  return {
    ...existingChannel,
    ...incomingChannel,
    name: existingChannel.name,
  };
};

const mergeServiceListQueue = (existingService: Service, incomingService: Service) => {
  const existingQueue = existingService.queue;
  const incomingQueue = incomingService.queue;

  if( !incomingQueue ) return existingQueue;
  if( incomingQueue.name ) return incomingQueue;
  if( !existingQueue?.name ) return incomingQueue;
  if( incomingQueue.id && existingQueue.id && incomingQueue.id !== existingQueue.id ) return incomingQueue;

  return {
    ...existingQueue,
    ...incomingQueue,
    name: existingQueue.name,
  };
};

const mergeServiceListUser = (existingService: Service, incomingService: Service) => {
  const existingUser = existingService.user;
  const incomingUser = incomingService.user;
  const existingQueueID = existingService.queue?.id;
  const incomingQueueID = incomingService.queue?.id;
  const queueChanged = Boolean( incomingQueueID && existingQueueID && incomingQueueID !== existingQueueID );

  if( incomingUser?.name ) return incomingUser;
  if( incomingUser ) {
    if( !existingUser?.name ) return incomingUser;
    if( incomingUser.id && existingUser.id && incomingUser.id !== existingUser.id ) return incomingUser;

    return {
      ...existingUser,
      ...incomingUser,
      name: existingUser.name,
    };
  }
  if( queueChanged ) return incomingUser;

  return existingUser;
};

const mergeServiceListContact = (existingService: Service, incomingService: Service) => {
  const existingContact = existingService.chat?.contact;
  const incomingContact = incomingService.chat?.contact;

  if( !incomingContact ) return existingContact;
  if( incomingContact.id && existingContact?.id && incomingContact.id !== existingContact.id ) return incomingContact;
  if( typeof incomingContact.isOnline === "boolean" ) return incomingContact;
  if( typeof existingContact?.isOnline !== "boolean" ) return incomingContact;

  return {
    ...incomingContact,
    isOnline: existingContact.isOnline,
  };
};

export const mergeRealtimeServiceList = (
  services: Array<Service>,
  incomingService: Service,
  status: ServiceStatus,
  queueId?: string,
  search?: string,
  role?: UserRole | null,
  channelId?: string,
): Array<Service> => {
  const existingService = services.find(( service ) => service.id === incomingService.id );
  const normalizedIncomingService = normalizeService( incomingService, role );
  const incomingMessages = normalizedIncomingService.chat.messages ?? [];
  const mergedIncomingService = existingService
    ? {
      ...normalizedIncomingService,
      queue: mergeServiceListQueue(existingService, normalizedIncomingService),
      user: mergeServiceListUser(existingService, normalizedIncomingService),
      chat: {
        ...normalizedIncomingService.chat,
        channel: mergeServiceListChannel(existingService, normalizedIncomingService),
        channelId: normalizedIncomingService.chat.channelId || existingService.chat.channelId || normalizedIncomingService.chat.channel?.id || existingService.chat.channel?.id,
        contact: mergeServiceListContact(existingService, normalizedIncomingService),
        messages: incomingMessages.length > 0
          ? mergeRealtimeMessages( existingService.chat.messages ?? [], incomingMessages )
          : existingService.chat.messages,
      },
    }
    : normalizedIncomingService;
  const filtered = services.filter(( service ) => service.id !== mergedIncomingService.id );

  if( !serviceMatchesRealtimeFilters( mergedIncomingService, status, queueId, search, channelId )) {
    return sortRealtimeServices( filtered );
  }

  return sortRealtimeServices([ mergedIncomingService, ...filtered ]);
};

export const promoteServiceAfterReply = (
  service: Service,
  message: Message,
  user?: User | null,
): Service => {
  const normalizedMessage = normalizeMessage( message, getAssignedRole( user ));
  const nextStatus = service.status === ServiceStatus.WAITING
    ? ServiceStatus.PROGRESS
    : service.status;

  return {
    ...service,
    status: nextStatus,
    user: nextStatus === ServiceStatus.PROGRESS && user ? user : service.user,
    chat: {
      ...service.chat,
      messages: [ normalizedMessage ],
    },
  };
};
